import type { ContentfulCollection, ContentfulModule } from 'libs/contentful/types';
import { capitalizeFirstLetter } from 'utils/formatters';

export type PageModel = 'Page' | 'PaidPage' | 'BlogPostPage' | 'PageBlogPostFlexible' | 'AgreementPage';
export type PageCollection =
  | 'pageCollection'
  | 'paidPageCollection'
  | 'blogPageCollection'
  | 'pageBlogPostFlexibleCollection'
  | 'pageAgreementCollection';

export const PAGE_MODEL_CONTENT_TYPE_IDS = {
  Page: 'page',
  PaidPage: 'paidPage',
  BlogPostPage: 'blogPage',
  PageBlogPostFlexible: 'pageBlogPostFlexible',
  AgreementPage: 'pageAgreement',
} as const satisfies Record<PageModel, string>;

export const PAGE_CONTENT_TYPE_IDS = Object.values(PAGE_MODEL_CONTENT_TYPE_IDS);

// A page content type's GraphQL `__typename` is its content type id with the first letter
// capitalized (e.g. `pageAgreement` → `PageAgreement`). Used to resolve rich-text entry
// hyperlinks that target any page type, not just the generic `Page`.
export const PAGE_LINK_TYPENAMES = PAGE_CONTENT_TYPE_IDS.map(capitalizeFirstLetter);

export const PAGE_LINK_TYPENAME_SET = new Set<string>(PAGE_LINK_TYPENAMES);

export type CombinedPageResponse<T> = {
  data: Partial<Record<PageCollection, ContentfulCollection<T>>>;
};

export type ModulesTypenamesResponse = CombinedPageResponse<{
  headerBannerCollection?: ContentfulCollection<
    Pick<ContentfulModule, '__typename' | 'sys'> & {
      ntExperiencesCollection?: {
        items?: Array<{ sys: { id: string } }>;
      };
    }
  >;
  hero?: Pick<ContentfulModule, '__typename' | 'sys'> & {
    ntExperiencesCollection?: {
      items?: Array<{ sys: { id: string } }>;
    };
  };
  nav?: Pick<ContentfulModule, '__typename' | 'sys'> & {
    ntExperiencesCollection?: {
      items?: Array<{ sys: { id: string } }>;
    };
  };
  modulesCollection?: ContentfulCollection<
    Pick<ContentfulModule, '__typename' | 'sys'> & {
      ntExperiencesCollection?: {
        items?: Array<{ sys: { id: string } }>;
      };
    }
  >;
  footer?: Pick<ContentfulModule, '__typename' | 'sys'> & {
    ntExperiencesCollection?: {
      items?: Array<{ sys: { id: string } }>;
    };
  };
  disclosuresCollection?: ContentfulCollection<
    Pick<ContentfulModule, '__typename' | 'sys'> & {
      ntExperiencesCollection?: {
        items?: Array<{ sys: { id: string } }>;
      };
    }
  >;
  pageVisualSettings?: Pick<ContentfulModule, '__typename' | 'sys'> & {
    ntExperiencesCollection?: {
      items?: Array<{ sys: { id: string } }>;
    };
  };
}>;

export interface PageModelConfig {
  pageModel: PageModel;
  pageCollection: PageCollection;
  staticPageFields: string;
  staticModuleFields: {
    headerBanner?: string;
    nav: string;
    hero: string;
    footer: string;
    disclosures: string;
  };
}

export interface PageConfig {
  pageCollection: PageCollection;
  typenames: string[];
  pageSectionIds: string[];
  hasExperiments: boolean;
  moduleExperiments: Map<string, boolean>;
  staticPageFields: string;
  staticModuleFields: PageModelConfig['staticModuleFields'];
}
